import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff, Mail, Lock, User } from 'lucide-react';
import { registerSchema, googleRegisterSchema } from '@/schemas/auth.schema';
import FormField from '@/pages/auth/components/FormField';
import { useRegister } from '@/hooks/useRegister';
import { Button, Checkbox, Input } from 'antd';
import AuthLayout from '@/layouts/AuthLayout';
import './Register.scss'

const Register = () => {
    const { loading, requestError, isGoogleRegister, googleData, handleRegister } = useRegister();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const schema = isGoogleRegister ? googleRegisterSchema : registerSchema;

    const {
        handleSubmit,
        control,
        setValue,
        formState: { errors },
    } = useForm({
        resolver: zodResolver(schema),
        defaultValues: {
            fname: googleData?.userFirstName ?? '',
            lname: googleData?.userLastName ?? '',
            email: googleData?.email ?? '',
            mobile: '',
            password: '',
            confirmPassword: '',
            isAccepted: false,
            rc: '',
        },
    });

    useEffect(() => {
        const rc = new URLSearchParams(window.location.search).get('rc');
        if (rc) setValue('rc', rc);
    }, [setValue]);

    return (
        <AuthLayout
            leftSideContent={
                <div className="auth-left-slot">
                    <p>Already have an account?</p>
                    <Link
                        className="mt-2 px-4"
                        to="/authentication/login"
                    >
                        Log In
                    </Link>
                </div>
            }
            footer={false}
        >
            <h3>Register</h3>
            <form onSubmit={handleSubmit(handleRegister)} noValidate>
                {requestError && (
                    <div className="form-error" role="alert">{requestError}</div>
                )}

                {/* Name row */}
                <div className="field-row">
                    <FormField label="First Name" error={errors.fname?.message} icon={User}>
                        <Controller
                            name="fname"
                            control={control}
                            render={({ field }) => (
                                <Input
                                    {...field}
                                    type="text"
                                    placeholder="First name"
                                    className="field-input"
                                    readOnly={isGoogleRegister}
                                    autoComplete="given-name"
                                />
                            )}
                        />
                    </FormField>

                    <FormField label="Last Name" error={errors.lname?.message} icon={User}>
                        <Controller
                            name="lname"
                            control={control}
                            render={({ field }) => (
                                <Input
                                    {...field}
                                    type="text"
                                    placeholder="Last name"
                                    className="field-input"
                                    readOnly={isGoogleRegister}
                                    autoComplete="family-name"
                                />
                            )}
                        />
                    </FormField>
                </div>

                {/* Email */}
                <FormField label="Email Address" error={errors.email?.message} icon={Mail}>
                    <Controller
                        name="email"
                        control={control}
                        render={({ field }) => (
                            <Input
                                {...field}
                                type="email"
                                placeholder="Email address"
                                className="field-input"
                                readOnly={isGoogleRegister}
                                autoComplete="email"
                            />
                        )}
                    />
                </FormField>

                {/* WhatsApp / Phone */}
                <div className={`phone-field-wrap ${errors.mobile ? 'has-error' : ''}`}>
                    <FormField label="Phone Number" error={errors.mobile?.message}>
                        <Controller
                            name="mobile"
                            control={control}
                            render={({ field }) => (
                                <Input
                                    {...field}
                                    type="tel"
                                    placeholder="e.g. 9876543210"
                                    className="field-input"
                                    style={{ paddingLeft: '12px' }}
                                />
                            )}
                        />
                    </FormField>
                </div>

                {/* Password fields — hidden for Google register */}
                {!isGoogleRegister && (
                    <div className="field-row">
                        <FormField
                            label="Password"
                            error={errors.password?.message}
                            icon={Lock}
                            rightElement={
                                <button type="button" className="field-icon-btn" onClick={() => setShowPassword(p => !p)}>
                                    {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                                </button>
                            }
                        >
                            <Controller
                                name="password"
                                control={control}
                                render={({ field }) => (
                                    <Input
                                        {...field}
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="Password"
                                        className="field-input"
                                        autoComplete="new-password"
                                    />
                                )}
                            />
                        </FormField>

                        <FormField
                            label="Confirm Password"
                            error={errors.confirmPassword?.message}
                            icon={Lock}
                            rightElement={
                                <button type="button" className="field-icon-btn" onClick={() => setShowConfirmPassword(p => !p)}>
                                    {showConfirmPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                                </button>
                            }
                        >
                            <Controller
                                name="confirmPassword"
                                control={control}
                                render={({ field }) => (
                                    <Input
                                        {...field}
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        placeholder="Confirm password"
                                        className="field-input"
                                        autoComplete="new-password"
                                    />
                                )}
                            />
                        </FormField>
                    </div>
                )}

                {/* Referral code — read only if from URL */}
                <Controller
                    name="rc"
                    control={control}
                    render={({ field }) =>
                        field.value ? (
                            <FormField label="Referral Code">
                                <Input
                                    {...field}
                                    type="text"
                                    className="field-input"
                                    style={{ paddingLeft: '12px' }}
                                    readOnly
                                />
                            </FormField>
                        ) : null
                    }
                />

                {/* Terms checkbox */}
                <div className="field-checkbox">
                    <Controller
                        name="isAccepted"
                        control={control}
                        render={({ field }) => (
                            <Checkbox 
                                id="isAccepted" 
                                {...field} 
                                checked={field.value}
                                onChange={(e) => field.onChange(e.target.checked)}
                            />
                        )}
                    />
                    <div>
                        <label htmlFor="isAccepted" className="field-checkbox-label">
                            I accept the <Link to="/terms-condition">terms</Link> and{' '}
                            <Link to="/privacy-policy">privacy policy</Link>
                        </label>
                        {errors.isAccepted && (
                            <span className="field-error" role="alert">{errors.isAccepted.message}</span>
                        )}
                    </div>
                </div>

                <Button 
                    htmlType="submit"
                    type="primary" 
                    className="auth-submit-btn" 
                >
                    {loading ? 'Creating account...' : 'Create Account'}
                </Button>

            </form>
        </AuthLayout>
    );
};

export default Register;
